/**
 * CSV Export Utility Functions
 * Reusable functions for exporting data to CSV format
 */

/**
 * Escape CSV value to handle special characters
 */
export function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Convert array of objects to CSV string
 */
export function convertToCSV(
  data: any[],
  headers: { key: string; label: string }[]
): string {
  if (!data || data.length === 0) {
    return "";
  }

  // Create header row
  const headerRow = headers.map((h) => escapeCSVValue(h.label)).join(",");

  // Create data rows
  const dataRows = data.map((item) => {
    return headers
      .map((header) => {
        const value = item[header.key];
        return escapeCSVValue(value);
      })
      .join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Trigger browser download of CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Format enrollment object for CSV export
 */
export function formatEnrollmentForCSV(enrollment: any): any {
  return {
    enrollmentId: enrollment._id || "",
    studentName:
      enrollment.user?.firstName && enrollment.user?.lastName
        ? `${enrollment.user.firstName} ${enrollment.user.lastName}`
        : enrollment.user?.username || "Unknown",
    studentEmail: enrollment.user?.email || "",
    courseName: enrollment.course?.title || "Unknown Course",
    enrollmentType: enrollment.isFree ? "Free" : "Paid",
    status: enrollment.status || "",
    enrollmentDate: enrollment.enrollmentDate
      ? new Date(enrollment.enrollmentDate).toLocaleDateString()
      : "",
    expiryDate: enrollment.enrollmentExpiresAt
      ? new Date(enrollment.enrollmentExpiresAt).toLocaleDateString()
      : "Lifetime",
    bandwidthConsumption: enrollment.watchTime?.formatted || "00:00:00",
    amountPaid: enrollment.isFree
      ? "0"
      : enrollment.paymentInfo?.amountPaid || "",
    currency: enrollment.isFree ? "" : enrollment.paymentInfo?.currency || "",
    paymentMethod: enrollment.isFree
      ? ""
      : enrollment.paymentInfo?.paymentMethod || "",
    progress: enrollment.progress?.percentage
      ? `${enrollment.progress.percentage}%`
      : "0%",
  };
}

/**
 * Format student object for CSV export
 */
export function formatStudentForCSV(student: any): any {
  return {
    studentId: student._id || "",
    username: student.username || "",
    firstName: student.firstName || "",
    lastName: student.lastName || "",
    fullName: `${student.firstName || ""} ${student.lastName || student.username || ""}`.trim(),
    email: student.email || "",
    phoneNumber: student.phoneNumber || "",
    role: student.role || "",
    isEmailVerified: student.isEmailVerified ? "Verified" : "Unverified",
    createdAt: student.createdAt ? new Date(student.createdAt).toLocaleDateString() : "",
    updatedAt: student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : "",
    isSuperAdmin: student.isSuperAdmin ? "Yes" : "No",
    permissionsCount: student.permissions?.length || 0,
  };
}

/**
 * Format a general user object for CSV export (used by ExportUsersDialog)
 */
export function formatUserForCSV(user: any): any {
  return {
    userId: user._id || "",
    username: user.username || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    fullName:
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.username ||
      "",
    email: user.email || "",
    phoneNumber: user.phoneNumber || "",
    role: user.role || "",
    isEmailVerified: user.isEmailVerified ? "Verified" : "Unverified",
    createdAt: user.createdAt
      ? new Date(user.createdAt).toLocaleDateString()
      : "",
    updatedAt: user.updatedAt
      ? new Date(user.updatedAt).toLocaleDateString()
      : "",
    isSuperAdmin: user.isSuperAdmin ? "Yes" : "No",
  };
}

/**
 * Format admin user object for CSV export (used by ExportAdminsDialog)
 */
export function formatAdminForCSV(admin: any): any {
  return {
    adminId: admin._id || "",
    username: admin.username || "",
    firstName: admin.firstName || "",
    lastName: admin.lastName || "",
    fullName:
      `${admin.firstName || ""} ${admin.lastName || ""}`.trim() ||
      admin.username ||
      "",
    email: admin.email || "",
    phoneNumber: admin.phoneNumber || "",
    isEmailVerified: admin.isEmailVerified ? "Verified" : "Unverified",
    isSuperAdmin: admin.isSuperAdmin ? "Yes" : "No",
    permissionsCount: admin.permissions?.length || 0,
    createdAt: admin.createdAt
      ? new Date(admin.createdAt).toLocaleDateString()
      : "",
    lastLogin: admin.lastLogin
      ? new Date(admin.lastLogin).toLocaleDateString()
      : "",
  };
}

/**
 * Format course object for CSV export (used by ExportCoursesDialog)
 */
export function formatCourseForCSV(course: any): any {
  const instructorUser = course.instructor?.user || null;
  return {
    courseId: course._id || "",
    title: course.title || "",
    slug: course.slug || "",
    status: course.isPublished ? "Published" : "Draft",
    level: course.level || "",
    language: course.language || "",
    category: course.trade?.name || "",
    instructor: instructorUser
      ? `${instructorUser.firstName || ""} ${instructorUser.lastName || ""}`.trim() ||
        instructorUser.username ||
        ""
      : "",
    isFree: course.isFree ? "Free" : "Paid",
    price: course.pricing?.price ?? course.price ?? 0,
    currency: course.pricing?.currency || course.currency || "",
    duration: course.duration || 0,
    totalLessons: course.totalLessons || 0,
    enrollmentCount: course.enrollmentCount || 0,
    rating: course.rating?.average ?? course.rating ?? 0,
    createdAt: course.createdAt
      ? new Date(course.createdAt).toLocaleDateString()
      : "",
    updatedAt: course.updatedAt
      ? new Date(course.updatedAt).toLocaleDateString()
      : "",
  };
}

/**
 * Format instructor object for CSV export (used by ExportInstructorsDialog)
 */
export function formatInstructorForCSV(instructor: any): any {
  const user = instructor.user || {};
  return {
    instructorId: instructor._id || "",
    username: user.username || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    fullName:
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.username ||
      "",
    email: user.email || "",
    phoneNumber: user.phoneNumber || "",
    isEmailVerified: user.isEmailVerified ? "Verified" : "Unverified",
    professionalTitle: instructor.professionalTitle || "",
    approvalStatus: instructor.approvalStatus || "",
    expertiseAreas: Array.isArray(instructor.expertiseAreas)
      ? instructor.expertiseAreas.join(", ")
      : "",
    yearsOfExperience: instructor.yearsOfExperience ?? 0,
    biography: instructor.biography || "",
    createdAt: instructor.createdAt
      ? new Date(instructor.createdAt).toLocaleDateString()
      : "",
    updatedAt: instructor.updatedAt
      ? new Date(instructor.updatedAt).toLocaleDateString()
      : "",
  };
}

/**
 * Generate timestamped filename for export
 */
export function generateTimestampedFilename(prefix: string): string {
  const now = new Date();
  const timestamp = now
    .toISOString()
    .replace(/T/, "_")
    .replace(/\..+/, "")
    .replace(/:/g, "-");
  return `${prefix}_${timestamp}.csv`;
}
